import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Container, Typography, Box, Paper, Alert, Button } from '@mui/material';
import { Info, CheckCircle } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { en } from '../translations/en';
import { UploadProvider } from '../contexts/UploadContext';
import { WizardCompletionProvider } from '../contexts/WizardCompletionContext';
import { FieldVisibilityProvider } from '../contexts/FieldVisibilityContext';
import { useUpload } from '../hooks/useUpload';
import { useCatalog } from '../hooks/useCatalog';
import { logger } from '../utils/logger';
import { WizardSidebar } from '../components/WizardSidebar';
import { AutosaveIndicator, AutosaveStatus } from '../components/AutosaveIndicator';
import { UploadTab } from './wizard/UploadTab';
import { DescribeTab } from './wizard/DescribeTab';
import { MediaTab } from './wizard/MediaTab';
import { MeasureTab } from './wizard/MeasureTab';
import { CatalogKeywordsTab } from './wizard/CatalogKeywordsTab';
import { ProvenanceTab } from './wizard/ProvenanceTab';
import { AdditionalInfoTab } from './wizard/AdditionalInfoTab';
import { ReviewTab } from './wizard/ReviewTab';
import { EditModeCompletionInitializer } from '../components/EditModeCompletionInitializer';
import { getProcessingStatus } from '../utils/processingApi';
import { AutosaveStatusContext } from '../contexts/contexts';
import { getCatalogLimits, CatalogLimits } from '../utils/limitsApi';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { useCollections } from '../hooks/useCollections';
import { useUploadImageVisibility } from '../hooks/useUploadImageVisibility';
import { ObjectPrivacyControls } from '../components/ObjectPrivacyControls';
import { computeImagePrivacyStatus } from '../utils/uploadUtils';
import { resolveCatalogAliIdForRequest } from '../utils/resolveCatalogAliIdForRequest';

const UploadObjectContent = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { catalogId, addImage, setCatalogId, uploadedImages, setSelectedCoverId, setAliCatalogNumber, setCatalogTitle, createCatalog } = useUpload();
  const { getCatalogItem, getKeywords, getDescription } = useCatalog();
  const { activeCatalogOwnerId, activeCatalogAliId, switchToOwnCatalog } = useActiveCatalog();
  const { selectCollection } = useCollections();

  // Compute effective owner: in edit mode, prefer explicit URL owner param over context.
  // This prevents stale delegate context from causing 404s when editing own items.
  const editId = searchParams.get('edit');
  const reuseId = searchParams.get('reuseId');
  const ownerParam = searchParams.get('owner');
  const isEditMode = !!editId;
  const isReuseMode = !!reuseId;
  const effectiveOwnerId = isEditMode ? (ownerParam || activeCatalogOwnerId) : activeCatalogOwnerId;

  const { toggleVisibility, setAllVisibility, error: imageVisibilityError } = useUploadImageVisibility(effectiveOwnerId);
  const [activeMainStep, setActiveMainStep] = useState(0);
  // GL#46: error state for draft-catalog mint failure (e.g. FK constraint on
  // brand-new Creator accounts — GL#49). Shown in place of advancing the wizard.
  const [mintError, setMintError] = useState<string | null>(null);

  const imagePrivacyStatus = useMemo(
    () => computeImagePrivacyStatus(uploadedImages),
    [uploadedImages]
  );

  // Scroll to top when switching wizard tabs (#1025)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeMainStep]);

  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [lastSaveTime, setLastSaveTime] = useState<Date | undefined>();
  // The active wizard tab registers its "flush pending save" handler here so
  // the "Finished" button can force an immediate save before navigating away.
  const saveNowRef = useRef<(() => Promise<void> | void) | null>(null);
  const registerSaveNow = useCallback((fn: (() => Promise<void> | void) | null) => {
    saveNowRef.current = fn;
  }, []);
  const [catalogLimits, setCatalogLimits] = useState<CatalogLimits | null>(null);
  // Session-only dismissal of the privacy notice (#1093). Reappears on full reload by design.
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(true);
  // Preloaded catalog item data shared with EditModeCompletionInitializer to avoid
  // a duplicate GET /api/catalogs/{id} call on wizard load. Fixes #1059.
  const [preloadedCatalogItem, setPreloadedCatalogItem] = useState<{ images?: unknown[]; coverImageId?: string | null; status?: string } | null>(null);
  const [editLoadError, setEditLoadError] = useState<string | null>(null);

  // Reset stale delegate context when editing without an explicit owner param.
  // This prevents 404s when a user views a delegated catalog and then navigates
  // to edit their own item.
  useEffect(() => {
    if (isEditMode && !ownerParam && activeCatalogOwnerId) {
      switchToOwnCatalog();
    }
  }, [isEditMode, ownerParam, activeCatalogOwnerId, switchToOwnCatalog]);

  // Reuse mode: set the catalog ID to the cleared item so new uploads go to the same slot.
  // The backend validates the item has status=CLEARED before accepting uploads.
  useEffect(() => {
    if (isReuseMode && reuseId) {
      setCatalogId(reuseId);
    }
  }, [isReuseMode, reuseId, setCatalogId]);

  // Note: Catalog items are created on-demand during initiateMultipartUpload
  // in useMultipartUpload hook. This prevents orphaned "Untitled" catalog items
  // when users visit the upload page but don't actually upload anything.
  // See bug #678 for details.

  // Prevent browser from navigating to dropped files when user misses the drop zone (#984).
  // The actual drop zone (ImageUploadArea) calls stopPropagation, so only stray drops reach here.
  useEffect(() => {
    const preventBrowserDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventBrowserDrop);
    document.addEventListener('drop', preventBrowserDrop);
    return () => {
      document.removeEventListener('dragover', preventBrowserDrop);
      document.removeEventListener('drop', preventBrowserDrop);
    };
  }, []);

  // Fetch catalog limits on mount
  useEffect(() => {
    getCatalogLimits(effectiveOwnerId)
      .then(setCatalogLimits)
      .catch(() => { /* limits are non-critical */ });
  }, [effectiveOwnerId]);

  // Track if edit mode data has been loaded (prevents duplicate calls in StrictMode)
  const editDataLoadedRef = useRef(false);

  // Reset catalogTitle when entering a new-item flow so stale values from a
  // previous session don't bleed through (MEDIUM-1 from review).
  useEffect(() => {
    if (!isEditMode) {
      setCatalogTitle('');
    }
  }, [isEditMode, setCatalogTitle]);

  // Load existing catalog data when in edit mode
  useEffect(() => {
    // Skip if not in edit mode, no edit ID, or already loaded
    if (!isEditMode || !editId || editDataLoadedRef.current) {
      return;
    }

    // Skip if images are already loaded (e.g., from a previous load)
    if (uploadedImages.length > 0) {
      editDataLoadedRef.current = true;
      return;
    }

    editDataLoadedRef.current = true;

    const loadExistingCatalog = async () => {
      try {
        // Set the catalog ID immediately
        setCatalogId(editId);

        // Get the full catalog item which includes all images.
        // Store the result in state so EditModeCompletionInitializer can reuse it
        // without making a second GET /api/catalogs/{id} request. Fixes #1059.
        const catalogItem = await getCatalogItem(editId, effectiveOwnerId);
        setPreloadedCatalogItem({ images: catalogItem.images, coverImageId: catalogItem.coverImageId ?? null, status: catalogItem.status });

        // Debug logging (dev only) - helps diagnose image loading issues
        logger.debug('[IMAGE_DEBUG] Catalog item raw response', catalogItem);
        logger.debug('[IMAGE_DEBUG] Images array', catalogItem.images);
        logger.debug('[IMAGE_DEBUG] Cover image ID', catalogItem.coverImageId);

        // Load ALL images from the catalog item
        if (catalogItem.images && catalogItem.images.length > 0) {
          catalogItem.images.forEach((image) => {
            // Get thumbnail URL from presigned URLs in the API response
            const thumbnailUrl = image.renditions?.find(r => r.size === 'thumb')?.url
              || image.renditions?.[0]?.url
              || image.url
              || null;

            addImage({
              imageId: image.imageId,
              file: null, // Existing images don't have File objects
              // GH#1258: FAILED must map to 'failed' (ImageGrid's failed
              // overlay), not 'processing' — otherwise a rejected image
              // shows a perpetual processing spinner over the original
              // instead of a visible failure state.
              status: image.status === 'READY' ? 'completed'
                : image.status === 'FAILED' ? 'failed'
                : 'processing',
              thumbnailUrl: thumbnailUrl,
              visibility: image.visibility || 'private',
              uploadedAt: image.uploadedAt ?? null,
            });

            logger.debug('[IMAGE_DEBUG] Edit mode: Added image to context', {
              imageId: image.imageId,
              thumbnailUrl,
              status: image.status
            });
          });

          // Set the cover image if specified
          if (catalogItem.coverImageId) {
            setSelectedCoverId(catalogItem.coverImageId);
          }

          // Load ALI catalog number from the catalog item or keywords
          if (catalogItem.aliCatalogNumber) {
            setAliCatalogNumber(catalogItem.aliCatalogNumber);
          } else {
            try {
              const keywords = await getKeywords(editId, effectiveOwnerId);
              if (keywords.aliCatalogNumber) {
                setAliCatalogNumber(keywords.aliCatalogNumber);
              }
            } catch (error) {
              logger.debug('Keywords not found for catalog item', error);
            }
          }

          // Load artwork title from description for wizard reminder card (#1021)
          try {
            const description = await getDescription(editId, effectiveOwnerId) as { title?: string };
            if (description.title) {
              setCatalogTitle(description.title);
            }
          } catch (error) {
            logger.debug('Description not found for catalog item', error);
          }
        } else if (catalogItem.status === 'CLEARED') {
          // CLEARED items are available-for-reuse slots — they have NO
          // images by definition. Skip the legacy getProcessingStatus
          // fallback entirely so we don't synthesize a phantom image from
          // stale renditions (see DA-55L.2488 regression). The downstream
          // tabs already handle status === 'CLEARED' via their own
          // placeholders (e.g. ReviewTab's "Available Catalog Number" card),
          // and the empty uploadedImages list lets the Upload step render
          // its normal empty drop zone, ready for reuse.
          logger.debug('[IMAGE_DEBUG] Edit mode: CLEARED item, skipping fallback');
        } else {
          // Fallback: try to get processing status for the catalog ID as a single image
          // This handles legacy items that may not have the images array populated
          try {
            const processingStatus = await getProcessingStatus(editId);
            const thumbnailUrl = processingStatus.thumbnailUrl ||
              processingStatus.renditions?.find(r => r.size === 'thumb')?.url ||
              processingStatus.renditions?.[0]?.url ||
              null;

            const fallbackImageId = catalogItem.coverImageId || editId;

            addImage({
              imageId: fallbackImageId,
              file: null,
              status: processingStatus.status === 'READY' ? 'completed'
                : processingStatus.status === 'FAILED' ? 'failed'
                : 'processing',
              thumbnailUrl: thumbnailUrl,
              visibility: 'private',
            });

            logger.debug('[IMAGE_DEBUG] Edit mode (fallback): Added single image', {
              imageId: fallbackImageId,
              thumbnailUrl,
              status: processingStatus.status
            });
          } catch (error) {
            logger.debug('Fallback processing status not available', error);
          }
        }
      } catch (error) {
        logger.error('Failed to load existing catalog data', error);
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          setEditLoadError('This item could not be found. It may belong to a different catalog.');
        } else {
          setEditLoadError('Failed to load this item. Please try refreshing the page.');
        }
        // Signal EditModeCompletionInitializer that the catalog item fetch is done
        // (even on failure) so it can proceed with an empty fallback. Fixes #1059.
        setPreloadedCatalogItem({ images: [], coverImageId: null });
      }
    };

    loadExistingCatalog();
  }, [isEditMode, editId, setCatalogId, addImage, uploadedImages.length, getCatalogItem, setSelectedCoverId, setAliCatalogNumber, getKeywords, getDescription, setCatalogTitle]);

  const { currentItem } = useCatalog();

  // Get the effective catalog ID from upload context, edit param, or current item
  const effectiveCatalogId = editId || catalogId || currentItem?.catalogItemId;

  const getPageTitle = (step: number): string => {
    const titles = [
      'Upload Images',
      'Describe Artwork',
      'Media',
      'Measurements',
      'Inventory & Albums',
      'Provenance & References',
      'Additional Information',
      'Review & Finish'
    ];
    return titles[step] || '';
  };

  // GL#46: intercept step changes so that advancing from the Images step (0)
  // to Artwork Details (1) with no catalogId mints a draft catalog first.
  // The backend accepts an empty title (Optional[str] = None), so we pass ''.
  // If the mint fails (e.g. GL#49 FK constraint for brand-new Creators), we
  // surface an error and keep the user on step 0 rather than leaving them
  // on a perpetual spinner.
  //
  // isMintingRef: in-flight guard prevents a double-mint when the user clicks
  // a sidebar step twice while the first createCatalog await is still pending.
  // A ref is used (not state) to avoid a stale-closure re-render race.
  const isMintingRef = useRef(false);
  const handleMainStepChange = useCallback(async (step: number) => {
    const isAdvancingFromImages = step === 1 && activeMainStep === 0;
    const hasCatalogId = !!(editId || catalogId || currentItem?.catalogItemId);
    if (isAdvancingFromImages && !hasCatalogId) {
      // Bail immediately if a mint is already in-flight (re-entrancy guard).
      if (isMintingRef.current) return;
      isMintingRef.current = true;
      setMintError(null);
      try {
        // Pass effectiveOwnerId so delegate mints land under the delegator's
        // account via the backend's resolve_delegate_create_target helper.
        // When there is no delegation (own-catalog create) effectiveOwnerId is
        // null and createCatalog omits the owner_id query param — fully
        // backwards-compatible with the non-delegated path.
        await createCatalog('', effectiveOwnerId);
      } catch (err) {
        logger.error('[UploadObject] Failed to mint draft catalog', err);
        setMintError('Could not create your catalog entry. Please try again.');
        isMintingRef.current = false;
        return; // Do not advance — keep user on step 0
      } finally {
        isMintingRef.current = false;
      }
    }
    setActiveMainStep(step);
  }, [activeMainStep, editId, catalogId, currentItem, createCatalog, effectiveOwnerId]);

  // FIX A (GH#1189): tracks whether a finish/save is in flight so the
  // Finished button can be disabled during the deferred write, preventing
  // a double-click from unblocking the first handleFinished before the title
  // has been written (re-entrancy / early-navigation bug).
  const [isFinishing, setIsFinishing] = useState(false);

  // "Finished" flushes the active tab's pending autosave before leaving so a
  // late edit (still inside the 2s debounce window) isn't lost. The save is
  // best-effort — a failure is logged but never blocks navigation.
  const handleFinished = useCallback(async () => {
    setIsFinishing(true);
    try {
      await saveNowRef.current?.();
    } catch (err) {
      logger.error('Save on Finished failed', err);
    } finally {
      setIsFinishing(false);
    }
    selectCollection(null);
    const incomingFrom = (location.state as { from?: string } | null)?.from;
    if (incomingFrom === 'object-details' && editId) {
      navigate(`/object-details/${editId}`);
    } else {
      navigate('/my-objects');
    }
  }, [selectCollection, navigate, location, editId]);

  const tabComponents = [
    <UploadTab
      key="upload"
      ownerId={effectiveOwnerId}
      // catalogAliId is only meaningful for own catalogs — delegated paths
      // mint under the delegator's primary via the owner_id route. The
      // helper centralizes this rule (also used by useCatalogKeywords) so
      // it can't drift across call sites.
      catalogAliId={resolveCatalogAliIdForRequest(activeCatalogAliId, effectiveOwnerId)}
      toggleVisibility={toggleVisibility}
      imageVisibilityError={imageVisibilityError}
    />,
    <DescribeTab key="describe" catalogItemId={effectiveCatalogId} ownerId={effectiveOwnerId} isEditMode={isEditMode} />,
    <MediaTab key="media" catalogItemId={effectiveCatalogId} ownerId={effectiveOwnerId} />,
    <MeasureTab key="measure" catalogItemId={effectiveCatalogId} ownerId={effectiveOwnerId} />,
    <CatalogKeywordsTab key="keywords" catalogItemId={effectiveCatalogId} ownerId={effectiveOwnerId} />,
    <ProvenanceTab key="provenance" catalogItemId={effectiveCatalogId} ownerId={effectiveOwnerId} itemStatus={preloadedCatalogItem?.status} />,
    <AdditionalInfoTab key="additional" catalogItemId={effectiveCatalogId} ownerId={effectiveOwnerId} />,
    <ReviewTab key="review" catalogItemId={effectiveCatalogId} ownerId={effectiveOwnerId} onNavigateToTab={setActiveMainStep} />
  ];

  return (
    <FieldVisibilityProvider catalogItemId={effectiveCatalogId || ''} ownerId={effectiveOwnerId}>
      <WizardCompletionProvider catalogId={effectiveCatalogId || ''}>
        {editId && <EditModeCompletionInitializer editId={editId} ownerId={effectiveOwnerId} preloadedCatalogItem={preloadedCatalogItem} />}
        <AutosaveStatusContext.Provider value={{ setStatus: setAutosaveStatus, setLastSaveTime, registerSaveNow }}>
          <Container maxWidth="xl">
      <Box sx={{ py: { xs: 1, sm: 2, md: 2 }, px: { xs: 1, sm: 2, md: 0 } }}>
        {/* Title row (#1093): title left, privacy notice right on the same line on md+.
            On xs/sm the notice drops under the title. No subtitle — keeps chrome compact. */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: { xs: 1.5, md: 2 },
        }}>
          <Typography variant="h5" component="h1" sx={{ flexShrink: 0 }}>
            {en.wizard.title}
          </Typography>
          {!editLoadError && showPrivacyNotice && (
            <Alert
              severity="info"
              icon={<Info />}
              onClose={() => setShowPrivacyNotice(false)}
              sx={{
                py: 0,
                bgcolor: theme.palette.action.hover,
                color: theme.palette.text.primary,
                '& .MuiAlert-icon': {
                  color: theme.palette.text.secondary,
                },
                '& .MuiAlert-message': {
                  py: 0.75,
                },
              }}
            >
              {en.wizard.privacyNoticeShort}
            </Alert>
          )}
        </Box>

        {catalogLimits?.maxItems != null && catalogLimits.remaining != null && catalogLimits.remaining <= 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            You have reached your catalog item limit ({catalogLimits.maxItems}). Delete existing items or upgrade your plan to add more.
          </Alert>
        )}
        {catalogLimits?.maxItems != null && catalogLimits.remaining != null && catalogLimits.remaining > 0 && catalogLimits.remaining <= Math.ceil(catalogLimits.maxItems * 0.1) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are approaching your catalog item limit: {catalogLimits.currentCount} / {catalogLimits.maxItems} items used ({catalogLimits.remaining} remaining).
          </Alert>
        )}
        {catalogLimits?.maxItems != null && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {catalogLimits.currentCount} / {catalogLimits.maxItems} catalog items used
          </Typography>
        )}

        {editLoadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {editLoadError}
            <Box sx={{ mt: 1 }}>
              <Button variant="text" size="small" onClick={() => navigate('/my-objects')}>
                Go to My Catalog
              </Button>
            </Box>
          </Alert>
        )}

        {/* GL#46: surface createCatalog failures so the user sees a message
            instead of a spinner, and can try again */}
        {mintError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMintError(null)}
            data-testid="mint-error-alert"
          >
            {mintError}
          </Alert>
        )}

        {!editLoadError && (<>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          minHeight: '500px'
        }}>
          {/* Sidebar */}
          <Box sx={{
            width: { xs: '100%', md: '300px' },
            flexShrink: 0,
            order: { xs: 1, md: 1 }
          }}>
            <WizardSidebar
              activeMainStep={activeMainStep}
              onMainStepChange={handleMainStepChange}
            />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, order: { xs: 2, md: 2 } }}>
            <Paper sx={{ minHeight: { xs: '300px', sm: '400px' }, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" component="h1" gutterBottom>
                      {getPageTitle(activeMainStep)}
                    </Typography>
                    {activeMainStep !== 7 && (
                      <ObjectPrivacyControls
                        imageStatus={imagePrivacyStatus}
                        onSetAllImagesPublic={() => setAllVisibility('public')}
                        onSetAllImagesPrivate={() => setAllVisibility('private')}
                      />
                    )}
                  </Box>
                  {activeMainStep !== 7 && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AutosaveIndicator status={autosaveStatus} lastSaveTime={lastSaveTime} />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={handleFinished}
                        disabled={isFinishing}
                        data-testid="wizard-finished-button"
                      >
                        {en.wizard.finished}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={{ flex: 1 }}>
                {tabComponents[activeMainStep]}
              </Box>
            </Paper>
          </Box>
        </Box>
        </>)}
      </Box>
          </Container>
        </AutosaveStatusContext.Provider>
      </WizardCompletionProvider>
    </FieldVisibilityProvider>
  );
};

export const UploadObject = () => {
  return (
    <UploadProvider>
      <UploadObjectContent />
    </UploadProvider>
  );
};
